/* eslint-disable react/jsx-no-useless-fragment */
import { FC, useEffect, useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { Center, Divider, Group, HoverCard, Skeleton, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import {
  IconCheck,
  IconChevronDown,
  IconChevronDownLeft,
  IconChevronRight,
  IconX,
} from '@tabler/icons';
import { useNavigate } from 'react-router-dom';
import { i18n } from '@lingui/core';
import { useDebouncedCallback } from 'use-debounce';
import { Planning, ResourceType, Shortage, TableResponsiveProps } from '../../../../Types/ResourceT';
import IncDecCounter from '../../../../Atoms/inputs/IncDecCounter';
import ResourcePlanningS from '../../../../Service/restapi/resourcePlanningS';
import { openDeleteResourcePlanning } from '../../../../Templates/Resource/EventPlanningsModals';
import PlanningActions from './PlanningActions';

interface Props extends TableResponsiveProps {
  planning: Planning;
  depth?: number;
  refresh: () => unknown;
  showPlanningIcon?: boolean;
  parentType?: ResourceType;
}

const PlanningTableRow: FC<Props> = ({
  planning: initialPlanning,
  depth = 0,
  refresh,
  showCategory = true,
  showType = true,
  showCounter = true,
  showPlanningIcon = true,
  parentType,
}) => {
  const [planning, setPlanning] = useState(initialPlanning);
  const { id: planningId, resource, plannedQuantity, children } = planning;
  const { id, type, name, content, category } = resource;
  const [opened, { toggle }] = useDisclosure(true);
  const navigate = useNavigate();
  const isItem = type === 'ITEM';

  const updateQuantity = useDebouncedCallback((quantity: number) => {
    ResourcePlanningS.updatePlanning(planningId, { plannedQuantity: quantity }).then((resJSON) => {
      if (resJSON) {
        setPlanning(resJSON);
        refresh();
      }
    });
  }, 1000);

  const onClick = () => navigate(`/resources/${id}/details`);

  const removePlanning = () => {
    const onConfirm = async () => {
      const deleted = await ResourcePlanningS.deletePlanning(planningId);
      if (deleted) {
        refresh?.();
      }
    };
    openDeleteResourcePlanning(name, onConfirm);
  };

  const mapSubRowUI = (_planning: Planning) => (
    <PlanningTableRow
      key={_planning.id}
      planning={_planning}
      depth={depth + 1}
      showCategory={showCategory}
      showType={showType}
      showCounter={showCounter}
      showPlanningIcon={showPlanningIcon && type !== 'CASE'}
      refresh={refresh}
      parentType={type}
    />
  );

  return (
    <>
      <tr>
        <td style={{ paddingLeft: depth * 10 }}>
          {!isItem && content.length > 0 && (
            <>
              {opened && <IconChevronDown size={20} onClick={toggle} style={{ cursor: 'pointer' }} />}
              {!opened && <IconChevronRight size={20} onClick={toggle} style={{ cursor: 'pointer' }} />}
            </>
          )}
          {isItem && depth > 0 && <IconChevronDownLeft size={20} />}
        </td>
        <td>
          {showPlanningIcon && (
            <Center>
              <PlanningStatusIcon planning={planning} />
            </Center>
          )}
        </td>
        <td className="overflow-ellipsis" onClick={onClick} style={{ cursor: 'pointer' }}>
          {name}
        </td>
        {showCategory && (
          <td onClick={onClick} style={{ cursor: 'pointer', maxWidth: 100 }} className="overflow-ellipsis">
            {category?.name}
          </td>
        )}
        {showType && (
          <td onClick={onClick} style={{ cursor: 'pointer' }}>
            {i18n._(`resource.type.${type}`)}
          </td>
        )}
        {showCounter && (
          <td align="center">
            {depth === 0 && (
              <IncDecCounter initialCount={plannedQuantity} onChanged={updateQuantity} onLastRemoved={removePlanning} />
            )}
            {depth > 0 && initialPlanning.plannedQuantity}
          </td>
        )}
        <td style={{ position: 'relative' }}>
          {(depth === 0 || (showPlanningIcon && type !== 'CASE')) && (
            <Center>
              <div style={{ position: 'absolute', right: 0 }}>
                <PlanningActions planning={planning} refresh={refresh} removePlanning={removePlanning} />
              </div>
            </Center>
          )}
        </td>
      </tr>
      {children && children.length > 0 && opened && children.map(mapSubRowUI)}
    </>
  );
};

const ShortageBreakdown: FC<{ shortages: Shortage[]; warehouse: boolean }> = ({ shortages, warehouse }) => {
  const totals = shortages
    .filter((s) => s.resourceType !== ResourceType.KIT)
    .reduce(
      (acc, s) => ({
        own: acc.own + s.own,
        planned: acc.planned + s.planned,
        inRepair: acc.inRepair + s.inRepair,
        inCase: acc.inCase + s.inCase,
        reserved: acc.reserved + s.reserved,
        subRented: acc.subRented + s.subRented,
        shortage: acc.shortage + (warehouse ? s.warehouseShortage : s.shortage),
      }),
      { own: 0, planned: 0, inRepair: 0, inCase: 0, reserved: 0, subRented: 0, shortage: 0 },
    );

  const rows: { label: string; value: number; subtract?: boolean }[] = [
    { label: 'Eigenbestand', value: totals.own },
    { label: 'Eingeplant', value: totals.planned, subtract: true },
    ...(totals.inRepair > 0 ? [{ label: 'In Reparatur', value: totals.inRepair, subtract: true }] : []),
    ...(totals.inCase > 0 ? [{ label: 'In Kiste', value: totals.inCase, subtract: true }] : []),
    ...(totals.reserved > 0 ? [{ label: 'Reserviert', value: totals.reserved, subtract: true }] : []),
    ...(totals.subRented > 0 ? [{ label: 'Untervermietet', value: totals.subRented }] : []),
  ];

  return (
    <Stack spacing={4} p={4}>
      <Text size="xs" weight={600}>Mangelaufschlüsselung</Text>
      <Divider />
      {rows.map((row) => (
        <Group key={row.label} position="apart" spacing="xl" noWrap>
          <Text size="xs" color="dimmed">{row.label}</Text>
          <Text size="xs" color={row.subtract ? 'red' : 'inherit'}>
            {row.subtract ? `−${row.value}` : `+${row.value}`}
          </Text>
        </Group>
      ))}
      <Divider />
      <Group position="apart" spacing="xl" noWrap>
        <Text size="xs" weight={600}>Mangel</Text>
        <Text size="xs" weight={600} color={totals.shortage < 0 ? 'red' : 'green'}>
          {totals.shortage}
        </Text>
      </Group>
    </Stack>
  );
};

const PlanningStatusIcon: FC<{ planning: Planning }> = ({ planning }) => {
  const { planningGroup, warehousePlanning, resource } = planning;
  const { entityId, entityType, begin, end } = planningGroup ?? {};
  const { id: resourceId } = resource;
  const [shortages, setShortages] = useState<Shortage[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchShortages = () => {
    setLoading(true);
    ResourcePlanningS.getShortages(resourceId, begin, end, entityType, entityId)
      .then(setShortages)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchShortages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planning]);

  if (loading) {
    return <Skeleton circle height={20} />;
  }

  if (warehousePlanning) {
    const hasShortage = shortages.reduce(
      (accumulator, currentShortage) =>
        accumulator ||
        (currentShortage.warehouseShortage < 0 && currentShortage.resourceType !== ResourceType.KIT) ||
        currentShortage.showWarehousePlanningShortage,
      false,
    );

    const icon = (
      <div>
        <ThemeIcon radius="xl" size="sm" color={hasShortage ? '#FF5C5A' : 'green'}>
          {hasShortage ? <IconX size={14} /> : <IconCheck size={14} />}
        </ThemeIcon>
      </div>
    );

    if (hasShortage) {
      return (
        <HoverCard width={220} shadow="md" openDelay={200} position="bottom-start">
          <HoverCard.Target>{icon}</HoverCard.Target>
          <HoverCard.Dropdown>
            <ShortageBreakdown shortages={shortages} warehouse />
          </HoverCard.Dropdown>
        </HoverCard>
      );
    }

    return (
      <Tooltip label="Resource ist eingeplant und ausreichend verfügbar" position="bottom-start" transition="slide-down">
        {icon}
      </Tooltip>
    );
  }

  const hasShortage = shortages.reduce(
    (accumulator, currentShortage) =>
      accumulator ||
      (currentShortage.shortage < 0 && currentShortage.resourceType !== ResourceType.KIT) ||
      currentShortage.showResourcePlanningShortage,
    false,
  );

  const icon = (
    <ThemeIcon radius="xl" size="sm" color={hasShortage ? '#FFD600' : 'gray'}>
      {hasShortage ? <IconX color="#4b525b" size={14} /> : <IconCheck color="black" size={14} />}
    </ThemeIcon>
  );

  if (hasShortage) {
    return (
      <HoverCard width={220} shadow="md" openDelay={200} position="bottom-start">
        <HoverCard.Target>{icon}</HoverCard.Target>
        <HoverCard.Dropdown>
          <ShortageBreakdown shortages={shortages} warehouse={false} />
        </HoverCard.Dropdown>
      </HoverCard>
    );
  }

  return (
    <Tooltip label="Resource ist verfügbar und muss noch bestätigt werden." position="bottom-start" transition="slide-down">
      {icon}
    </Tooltip>
  );
};

export default PlanningTableRow;
